/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Internal dependencies
 */
import clamp from '../../../utils/clamp';
import {
  DEFAULT_TEXT_LIST_STYLE,
  TEXT_LIST_MARKER_GLYPHS,
  TEXT_LIST_STYLE_LIMITS,
  TEXT_LIST_STYLE_VERSION,
} from '../constants';

const MARKER_PRESETS = ['disc', 'circle', 'square', 'custom'];
const MARKER_SHAPE_PRESETS = ['disc', 'circle', 'square'];
const MARKER_SHAPE_SIZE_RATIO = 0.375;
const CIRCLE_STROKE_WIDTH_RATIO = 0.18;

const round = (value) => Math.round(value * 10000) / 10000;

export function hasSemanticList(content = '') {
  return /<(?:ul|ol|li)(?:\s|>)/i.test(content);
}

export function isSingleGrapheme(value) {
  if (typeof value !== 'string' || value.trim() !== value || !value) {
    return false;
  }

  if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
    const segments = Array.from(
      new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(value)
    );
    return segments.length === 1;
  }

  return Array.from(value).length === 1;
}

function finiteOrDefault(value, defaultValue, limits) {
  if (!Number.isFinite(value)) {
    return defaultValue;
  }
  return clamp(value, limits);
}

function getMarker(marker = {}, fontSize) {
  const sourceMarker =
    marker && typeof marker === 'object' && !Array.isArray(marker)
      ? marker
      : {};
  const preset = MARKER_PRESETS.includes(sourceMarker.preset)
    ? sourceMarker.preset
    : DEFAULT_TEXT_LIST_STYLE.marker.preset;
  const glyph =
    preset === 'custom' && isSingleGrapheme(sourceMarker.glyph)
      ? sourceMarker.glyph
      : undefined;
  const markerSize =
    Number.isFinite(sourceMarker.sizeRatio) && sourceMarker.sizeRatio > 0
      ? round(
          clamp(
            fontSize * sourceMarker.sizeRatio,
            TEXT_LIST_STYLE_LIMITS.markerSize
          )
        )
      : sourceMarker.size;
  const size = Number.isFinite(markerSize)
    ? finiteOrDefault(markerSize, fontSize, TEXT_LIST_STYLE_LIMITS.markerSize)
    : undefined;
  const baselineShift = Number.isFinite(sourceMarker.baselineShift)
    ? finiteOrDefault(
        sourceMarker.baselineShift,
        0,
        TEXT_LIST_STYLE_LIMITS.markerBaselineShift
      )
    : undefined;
  const horizontalShift =
    preset === 'custom' &&
    glyph &&
    Number.isFinite(sourceMarker.horizontalShift)
      ? finiteOrDefault(
          sourceMarker.horizontalShift,
          0,
          TEXT_LIST_STYLE_LIMITS.markerHorizontalShift
        )
      : undefined;

  return {
    preset: preset === 'custom' && !glyph ? 'disc' : preset,
    ...(glyph ? { glyph } : null),
    ...(sourceMarker.color?.color ? { color: sourceMarker.color } : null),
    ...(size ? { size } : null),
    ...(Number.isFinite(sourceMarker.sizeRatio) && sourceMarker.sizeRatio > 0
      ? { sizeRatio: sourceMarker.sizeRatio }
      : null),
    ...(baselineShift ? { baselineShift } : null),
    ...(horizontalShift ? { horizontalShift } : null),
  };
}

export function getNormalizedTextListStyle(listStyle, fontSize = 36) {
  const source =
    listStyle?.version === TEXT_LIST_STYLE_VERSION ? listStyle : {};

  return {
    version: TEXT_LIST_STYLE_VERSION,
    marker: getMarker(source.marker, fontSize),
    indent: finiteOrDefault(
      source.indent,
      DEFAULT_TEXT_LIST_STYLE.indent,
      TEXT_LIST_STYLE_LIMITS.indent
    ),
    nestedIndent: finiteOrDefault(
      source.nestedIndent,
      DEFAULT_TEXT_LIST_STYLE.nestedIndent,
      TEXT_LIST_STYLE_LIMITS.nestedIndent
    ),
    markerGap: finiteOrDefault(
      source.markerGap,
      DEFAULT_TEXT_LIST_STYLE.markerGap,
      TEXT_LIST_STYLE_LIMITS.markerGap
    ),
    itemSpacing: finiteOrDefault(
      source.itemSpacing,
      DEFAULT_TEXT_LIST_STYLE.itemSpacing,
      TEXT_LIST_STYLE_LIMITS.itemSpacing
    ),
  };
}

export function getListMarkerGlyph(listStyle, listType, itemNumber = 1) {
  if (listType === 'ordered') {
    return `${itemNumber}.`;
  }

  const { marker } = getNormalizedTextListStyle(listStyle);
  return marker.glyph || TEXT_LIST_MARKER_GLYPHS[marker.preset] || '•';
}

export function getOrderedListMarkerColumnWidth(
  itemCount,
  measureMarkerWidth,
  fallbackWidth
) {
  let widestMarker = fallbackWidth;
  const normalizedItemCount = Math.max(0, Math.floor(itemCount));

  for (let itemNumber = 1; itemNumber <= normalizedItemCount; itemNumber++) {
    const measuredWidth = measureMarkerWidth(`${itemNumber}.`);
    if (Number.isFinite(measuredWidth) && measuredWidth > widestMarker) {
      widestMarker = measuredWidth;
    }
  }

  return widestMarker;
}

export function getListMarkerShape(listStyle, fontSize = 36, lineHeight = 1) {
  const { marker } = getNormalizedTextListStyle(listStyle, fontSize);
  if (!MARKER_SHAPE_PRESETS.includes(marker.preset)) {
    return null;
  }

  const numericLineHeight = Number(lineHeight);
  const resolvedLineHeight =
    Number.isFinite(numericLineHeight) && numericLineHeight > 0
      ? numericLineHeight
      : 1;
  const size = (marker.size || fontSize) * MARKER_SHAPE_SIZE_RATIO;

  return {
    preset: marker.preset,
    size: round(size),
    offset: round(
      (fontSize * resolvedLineHeight - size) / 2 - (marker.baselineShift || 0)
    ),
    strokeWidth:
      marker.preset === 'circle' ? round(size * CIRCLE_STROKE_WIDTH_RATIO) : 0,
  };
}

export function scaleTextListStyle(listStyle, scale, fontSize) {
  if (!listStyle || !Number.isFinite(scale) || scale === 1) {
    return listStyle;
  }

  const normalized = getNormalizedTextListStyle(listStyle, fontSize);
  return getNormalizedTextListStyle(
    {
      ...normalized,
      marker: {
        ...normalized.marker,
        ...(normalized.marker.size
          ? { size: normalized.marker.size * scale }
          : null),
        ...(normalized.marker.baselineShift
          ? { baselineShift: normalized.marker.baselineShift * scale }
          : null),
        ...(normalized.marker.horizontalShift
          ? { horizontalShift: normalized.marker.horizontalShift * scale }
          : null),
      },
      indent: normalized.indent * scale,
      nestedIndent: normalized.nestedIndent * scale,
      markerGap: normalized.markerGap * scale,
      itemSpacing: normalized.itemSpacing * scale,
    },
    fontSize * scale
  );
}

// Authoring metadata controls recalculation; renderers still consume numeric size.
export function syncTextListMarkerSize(element) {
  const ratio = element.listStyle?.marker?.sizeRatio;
  if (
    element.type !== 'text' ||
    element.listStyle?.version !== TEXT_LIST_STYLE_VERSION ||
    !Number.isFinite(ratio) ||
    ratio <= 0 ||
    !Number.isFinite(element.fontSize) ||
    element.fontSize <= 0
  ) {
    return element;
  }
  const size = round(
    clamp(element.fontSize * ratio, TEXT_LIST_STYLE_LIMITS.markerSize)
  );
  if (size === element.listStyle.marker.size) {
    return element;
  }
  return {
    ...element,
    listStyle: {
      ...element.listStyle,
      marker: { ...element.listStyle.marker, size },
    },
  };
}

export function setTextListMarkerSize(marker, size) {
  // Merely focusing/blurring the size field must not count as an override.
  if (size !== undefined && size === marker.size) {
    return marker;
  }
  const { sizeRatio, ...rest } = marker;
  return { ...rest, size };
}
